import { type ReactElement } from 'react'
import { filterArchived, type AppId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { useConnectApp, useConnectInstalls, useProfile, useTranslation } from '@front/model'
import { NestedNavigation, NotReady, type PageRoute } from '@components'
import {
  buildConnectInstallMetricsHref,
  buildConnectInstallationEditorHref,
  buildConnectMarketplaceAppHref,
  buildConnectMarketplaceDocumentationHref,
  parseConnectAppId,
} from '../integration/util'
import { ConnectAccessGuard } from '../integration/access'

function MarketplaceAppLayout(): ReactElement {
  const { appId } = Route.useParams()
  const parsedAppId = parseConnectAppId(appId)

  if (!parsedAppId) {
    return (
      <ConnectAccessGuard>
        <NotReady error='Invalid app id' id='marketplace app' />
      </ConnectAccessGuard>
    )
  }

  return <MarketplaceAppNavigation appId={parsedAppId} />
}

function MarketplaceAppNavigation({ appId }: { appId: AppId }): ReactElement {
  const { t } = useTranslation()
  const { company } = useProfile()
  const appState = useConnectApp(appId)
  const installsState = useConnectInstalls()
  const install = filterArchived(installsState.data).find(
    (item) => item.appId === appId && (!company?.companyId || item.companyId === company.companyId)
  )

  const routes: PageRoute[] = [
    { path: buildConnectMarketplaceAppHref(appId), label: t('Details'), icon: 'info', matchFormat: 'exact' },
    {
      path: buildConnectMarketplaceDocumentationHref(appId),
      label: t('Documentation'),
      icon: 'description',
      matchFormat: 'exact',
    },
  ]

  if (install) {
    routes.push(
      {
        path: buildConnectInstallationEditorHref(appId, install.installId),
        label: t('Installation'),
        icon: 'settings',
        matchFormat: 'exact',
      },
      {
        path: buildConnectInstallMetricsHref(appId, install.installId),
        label: t('Metrics'),
        icon: 'monitoring',
        matchFormat: 'exact',
      }
    )
  }

  return (
    <ConnectAccessGuard>
      <NestedNavigation
        loading={appState.isLoading || installsState.isLoading}
        routes={routes}
        sectionLabel={appState.data?.app.name ?? t('App')}
      />
    </ConnectAccessGuard>
  )
}

export const Route = createFileRoute('/integration/marketplace_/$appId')({
  component: MarketplaceAppLayout,
})
